import React, { useRef, useState, useEffect } from 'react';

const r3 = (x) => Math.round(x * 1000) / 1000;
const MIN_WORD = 0.05;     // a word can't be shrunk below this
const CUT_THRESH = 0.12;   // gap < this -> render a shared CUT; >= -> a gap BAND (matches 重算)

// Source-coordinate waveform. Boundary-based editing: between every adjacent word
// pair we render ONE control — a shared CUT handle when the words abut (gap <
// 0.12s, dragging it moves both sides = automatic carry), or a draggable GAP BAND
// when there's real silence (gap >= 0.12s, preserving the gap, matching what 重算
// emits). Outer segment edges (first.start / last.end) stay as separate handles.
export default function Waveform({ waveformUrl, playTime, words, sentenceId,
                                  getTimes, setTimes, selWord, onSeek,
                                  axisStart = 0, axisDur, origin = 0, bounds, setBounds, flashAt = 0, locked = false }) {
  const ref = useRef(null);
  const draggedRef = useRef(false);
  const [bDrag, setBDrag] = useState(null);        // outer-bounds drag {side}
  const [bPrev, setBPrev] = useState(null);
  const bPrevRef = useRef(null);
  const [bd, setBd] = useState(null);              // boundary drag {i, kind, a, b, grab}
  const [bdPrev, setBdPrev] = useState(null);      // {aEnd, bStart} live preview for boundary i
  const bdPrevRef = useRef(null);

  const axis = axisDur > 0 ? axisDur : 1;
  const pct = (srcT) => Math.min(100, Math.max(0, ((srcT - axisStart) / axis) * 100));
  const tOf = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    return Math.max(axisStart, Math.min(axisStart + axis, axisStart + ((clientX - r.left) / r.width) * axis));
  };
  const T = (i) => (getTimes ? getTimes(sentenceId, i, words[i]) : words[i]);   // clip-relative {start,end}

  // Manual editing is WORD-SCOPED: only the SELECTED word's two boundaries are live.
  // Per EDGE: its LEFT boundary (selWord-1) gives the ONSET — the cut, or the band's
  // RIGHT edge (= word.start); its RIGHT boundary (selWord) the OFFSET — the cut, or
  // the band's LEFT edge (= word.end). Everything else (other cuts/edges, outer bounds)
  // is inert. No word selected → only the outer bounds are draggable (segment mode).
  const wordSel = selWord != null && selWord >= 0;
  const editKindOf = (i, isCut) => {
    if (locked || !wordSel) return null;
    if (i === selWord - 1) return isCut ? 'cut' : 'band-right';
    if (i === selWord) return isCut ? 'cut' : 'band-left';
    return null;
  };

  // ── boundary drag: cut | band-left | band-right (per-edge gating is on the handlers) ──
  function beginBd(i, kind, e) {
    if (locked) return;
    e.preventDefault(); e.stopPropagation();
    const a = T(i), b = T(i + 1);
    const init = { aEnd: a.end, bStart: b.start };
    bdPrevRef.current = init; setBdPrev(init); draggedRef.current = false;
    setBd({ i, kind, a, b, grab: tOf(e.clientX) - origin, init });
  }
  useEffect(() => {
    if (!bd) return;
    const { i, kind, a, b, grab, init } = bd;
    const move = (e) => {
      draggedRef.current = true;
      const t = tOf(e.clientX) - origin;
      let aEnd = init.aEnd, bStart = init.bStart;
      if (kind === 'cut') {                         // one shared boundary; both sides follow
        const mw = Math.min(MIN_WORD, (b.end - a.start) / 2.2);   // crammed words: shrink the floor so the cut never freezes / zero-widths a neighbour
        const v = r3(Math.max(a.start + mw, Math.min(b.end - mw, t)));
        aEnd = v; bStart = v;
      } else if (kind === 'band-left') {            // word i's offset; up to bStart (gap can close)
        aEnd = r3(Math.max(a.start + MIN_WORD, Math.min(init.bStart, t)));
      } else if (kind === 'band-right') {           // word i+1's onset; down to aEnd
        bStart = r3(Math.min(b.end - MIN_WORD, Math.max(init.aEnd, t)));
      }
      const p = { aEnd, bStart }; bdPrevRef.current = p; setBdPrev(p);
    };
    const up = () => {
      const p = bdPrevRef.current;
      setTimes(sentenceId, i, a.start, p.aEnd);       // word i keeps its start, new end
      setTimes(sentenceId, i + 1, p.bStart, b.end);   // word i+1 keeps its end, new start
      setBd(null); setBdPrev(null);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bd]);

  // ── outer bounds handle drag (segment edges) — word-scoped: gated at the handle to
  //    the FIRST word (start) / LAST word (end); never free-drag. ──
  function beginBDrag(side, e) {
    if (locked) return;
    e.preventDefault(); e.stopPropagation();
    bPrevRef.current = { ...bounds }; setBPrev({ ...bounds });
    setBDrag({ side });
  }
  useEffect(() => {
    if (!bDrag) return;
    const { side } = bDrag;
    const move = (e) => {
      const t = tOf(e.clientX); const cur = bPrevRef.current;
      const next = side === 'start'
        ? { start: r3(Math.min(t, cur.end - 0.02)), end: cur.end }
        : { start: cur.start, end: r3(Math.max(t, cur.start + 0.02)) };
      bPrevRef.current = next; setBPrev(next);
    };
    const up = () => {
      const v = bPrevRef.current;
      if (setTimes && words && words.length && side === 'end') {       // right edge = move the LAST word's end (a word edit, no :-1 override)
        const li = words.length - 1;
        setTimes(sentenceId, li, T(li).start, r3(v.end - origin));
      } else if (setTimes && words && words.length && side === 'start') {  // left edge = move the FIRST word's start
        setTimes(sentenceId, 0, r3(v.start - origin), T(0).end);
      } else if (setBounds) setBounds(v.start, v.end);
      setBDrag(null); setBPrev(null);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bDrag]);

  function handleSeek(e) {
    if (draggedRef.current) { draggedRef.current = false; return; }
    if (bd || bDrag) return;
    if (onSeek) onSeek(r3(tOf(e.clientX)));
  }

  if (!waveformUrl) return null;
  const bb = bPrev || bounds || { start: axisStart, end: axisStart + axis };

  // boundary descriptors (use the live preview for the one being dragged)
  const boundaries = [];
  if (setTimes && words && words.length > 1) {
    for (let i = 0; i < words.length - 1; i++) {
      let aEnd = T(i).end, bStart = T(i + 1).start;
      const dragging = bd && bd.i === i;
      if (dragging && bdPrev) { aEnd = bdPrev.aEnd; bStart = bdPrev.bStart; }
      // freeze the control type while dragging (don't flip cut<->band mid-drag)
      const isCut = dragging ? (bd.kind === 'cut') : ((bStart - aEnd) < CUT_THRESH);
      boundaries.push({ i, aEnd, bStart, isCut, dragging });
    }
  }

  const selT = (selWord >= 0 && words && words[selWord]) ? T(selWord) : null;
  // Outer bounds are word-scoped too: segment START = the FIRST word's left edge (live
  // only when word 0 is selected); segment END = the LAST word's right edge.
  // Outer edges edit the FIRST/LAST word directly (setTimes), never the :-1 play-range override
  // (v68 made the border derive from words, so dragging an edge no longer creates stray overrides).
  const startLive = !locked && words && words.length > 0 && selWord === 0;
  const endLive = !locked && words && words.length > 0 && selWord === words.length - 1;

  return (
    <>
    <div className={`wave${locked ? ' wave-locked' : ''}`} ref={ref} onClick={handleSeek}>
      <img className="wave-img" src={waveformUrl} alt="waveform" />
      <div className="wave-outside" style={{ left: 0, width: pct(bb.start) + '%' }} />
      <div className="wave-outside" style={{ left: pct(bb.end) + '%', width: (100 - pct(bb.end)) + '%' }} />

      {/* active word (clicked) — light context highlight, not editable */}
      {selT && (
        <div className="wave-wordlit" style={{ left: pct(origin + selT.start) + '%', width: (pct(origin + selT.end) - pct(origin + selT.start)) + '%' }} />
      )}

      {/* internal boundaries: shared cut OR gap band. Only the selected word's two
          are live; the rest render inert (greyed/thin, no handlers). */}
      {boundaries.map(({ i, aEnd, bStart, isCut, dragging }) => {
        const ek = editKindOf(i, isCut);
        if (isCut) {
          const live = ek === 'cut';
          const cutSrc = origin + (aEnd + bStart) / 2;
          return (
            <div key={i} className={`wave-cut-handle${dragging ? ' dragging' : ''}${live ? '' : ' wave-cut-inert'}`} style={{ left: pct(cutSrc) + '%' }}
              onMouseDown={live ? (e) => beginBd(i, 'cut', e) : undefined} title={live ? '拖曳：詞界（兩側同動）' : ''} />
          );
        }
        const l = pct(origin + aEnd), r = pct(origin + bStart);
        const leftLive = ek === 'band-left', rightLive = ek === 'band-right';
        return (
          <div key={i} className={`wave-gap${dragging ? ' dragging' : ''}${ek ? '' : ' wave-gap-inert'}`} style={{ left: l + '%', width: Math.max(0, r - l) + '%' }}>
            <div className={`wave-gap-edge wave-gap-l${leftLive ? '' : ' wave-gap-edge-inert'}`} onMouseDown={leftLive ? (e) => beginBd(i, 'band-left', e) : undefined} title={leftLive ? '拖曳：詞尾' : ''} />
            <div className={`wave-gap-edge wave-gap-r${rightLive ? '' : ' wave-gap-edge-inert'}`} onMouseDown={rightLive ? (e) => beginBd(i, 'band-right', e) : undefined} title={rightLive ? '拖曳：詞首' : ''} />
          </div>
        );
      })}

      {/* outer segment edges — word-scoped: start live only when the FIRST word is
          selected (its left edge), end live only when the LAST word is selected. */}
      {setBounds && (
        <>
          <div className={`wave-bound wave-bound-start${bDrag && bDrag.side === 'start' ? ' wave-bound-drag' : ''}${startLive ? '' : ' wave-bound-inert'}`} style={{ left: pct(bb.start) + '%' }}
            onMouseDown={startLive ? (e) => beginBDrag('start', e) : undefined} title={startLive ? '拖曳：詞首（片段開始）' : ''} />
          <div className={`wave-bound wave-bound-end${bDrag && bDrag.side === 'end' ? ' wave-bound-drag' : ''}${endLive ? '' : ' wave-bound-inert'}`} style={{ left: pct(bb.end) + '%' }}
            onMouseDown={endLive ? (e) => beginBDrag('end', e) : undefined} title={endLive ? '拖曳：詞尾（片段結束）' : ''} />
        </>
      )}

      {playTime > 0 && <div className="wave-playhead" style={{ left: pct(playTime) + '%' }} />}
      {flashAt > 0 && (
        <div className="wave-flash" key={flashAt}>
          {(words || []).map((w, i) => {
            const t = getTimes ? getTimes(sentenceId, i, w) : w;
            return <div key={i} className="wave-cut" style={{ left: pct(origin + t.start) + '%' }} />;
          })}
        </div>
      )}
    </div>
    </>
  );
}
